import { CheckIcon } from "@heroicons/react/24/outline";
import { Command as CommandPrimitive } from "cmdk";
import { useState, useRef, useCallback, type KeyboardEvent } from "react";
import React from "react";
import {
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { useDialogPortalContainer } from "@/core/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { focusNextTabbable } from "./focus-next-tabbable";

type Props<T extends string> = {
  id?: string;
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  renderOption?: (option: { value: T; label: string }) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
  persistentLabel?: boolean;
  className?: string;
  scrollAreaClassName?: string;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
  selectedLabel?: string;
};

export const BackendAutoComplete = React.forwardRef<
  HTMLInputElement,
  Props<string>
>(
  (
    {
      id,
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      renderOption,
      isLoading,
      emptyMessage = "No items.",
      placeholder = "Search...",
      label,
      fullWidth,
      required,
      disableRequirementAsterisk,
      persistentLabel,
      className,
      scrollAreaClassName,
      disabled,
      disabledTooltip,
      selectedLabel,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    // Check if we're inside a dialog
    const dialogPortalContainer = useDialogPortalContainer();

    // Combine the forwarded ref with our internal ref
    React.useImperativeHandle(ref, () => inputRef.current!, []);

    // Display value: show selectedLabel when blurred and something is selected, otherwise show searchValue
    const displayValue =
      !isFocused && selectedValue && selectedLabel
        ? selectedLabel
        : searchValue;

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) return;

        if (event.key === "Tab") return;

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Escape") {
          input.blur();
        }
      },
      [isOpen],
    );

    const handleInputKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          focusNextTabbable(event.currentTarget, event.shiftKey);
        }
      },
      [],
    );

    const handleFocus = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setIsFocused(true);
    }, [disabled]);

    const handleInputClick = useCallback(() => {
      if (disabled || isFocused) return;
      setOpen(true);
      setIsFocused(true);
    }, [disabled, isFocused]);

    const handleBlur = useCallback(() => {
      setOpen(false);
      setIsFocused(false);
    }, []);

    const handleSelectOption = useCallback(
      (value: string) => {
        const selectedItem = items.find((item) => item.value === value);
        onSelectedValueChange(value);
        if (selectedItem) {
          onSearchValueChange(selectedItem.label);
        }
        setOpen(false);
        setIsFocused(false);
      },
      [items, onSearchValueChange, onSelectedValueChange],
    );

    // Add asterisk to placeholder if required
    const placeholderWithAsterisk = required ? `${placeholder} *` : placeholder;

    // Return early with a disabled input if disabled
    if (disabled) {
      const disabledInput = (
        <div className={cn("relative p-px", fullWidth && "w-full")}>
          <div className="relative">
            <input
              id={id}
              disabled
              value={searchValue}
              placeholder={placeholderWithAsterisk}
              className={cn(
                "flex h-14 w-full cursor-not-allowed rounded-lg border border-stroke-tertiary bg-bg-secondary px-4 text-lg text-tertiary-text shadow-sm transition-colors",
                searchValue && label && "pb-2 pt-6",
                "placeholder:text-lg placeholder:text-tertiary-text",
                className,
              )}
            />
            {label && (persistentLabel || searchValue) && (
              <Typography
                variant="subtitleW3"
                className={cn(
                  "pointer-events-none absolute left-4 top-2 text-tertiary-text",
                )}
              >
                {label}
                {required && !disableRequirementAsterisk && (
                  <span className="ml-1">*</span>
                )}
              </Typography>
            )}
          </div>
        </div>
      );

      if (disabledTooltip) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>{disabledInput}</TooltipTrigger>
            <TooltipContent>{disabledTooltip}</TooltipContent>
          </Tooltip>
        );
      }

      return disabledInput;
    }

    const inputElement = (
      <div className={cn("relative p-px", fullWidth && "w-full")}>
        <CommandPrimitive
          onKeyDown={handleKeyDown}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          onMouseLeave={() => setHighlightedValue("")}
          shouldFilter={false}
        >
          <div className="relative">
            <CommandPrimitive.Input
              id={id}
              ref={inputRef}
              value={displayValue}
              onValueChange={onSearchValueChange}
              onBlur={handleBlur}
              autoFocus={false}
              onFocus={handleFocus}
              onClick={handleInputClick}
              onKeyDownCapture={handleInputKeyDownCapture}
              placeholder={placeholderWithAsterisk}
              className={cn(
                "flex h-14 w-full rounded-lg border border-input bg-transparent px-4 text-lg shadow-sm transition-colors",
                displayValue && label && "pb-2 pt-6",
                isLoading && "pr-10",
                "placeholder:text-lg placeholder:text-tertiary-text",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:border-stroke-tertiary disabled:bg-bg-secondary disabled:text-tertiary-text",
                className,
              )}
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
            {label && (persistentLabel || displayValue) && (
              <Typography
                variant="subtitleW3"
                className="absolute left-4 top-2 text-tertiary-text"
              >
                {label}
                {required && !disableRequirementAsterisk && (
                  <span className="ml-1">*</span>
                )}
              </Typography>
            )}
          </div>
          <div className="relative mt-1">
            <div
              className={cn(
                "absolute top-0 w-full rounded-xl bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] outline-none",
                dialogPortalContainer ? "z-[9999]" : "z-50",
                isOpen ? "block" : "hidden",
              )}
            >
              <div className="rounded-lg ring-1 ring-slate-200">
                <div
                  className={cn(
                    "scrollbar-hidden max-h-[170px] overflow-y-auto",
                    scrollAreaClassName,
                  )}
                >
                  <CommandList>
                    {items.length > 0 ? (
                      <CommandGroup>
                        {items.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onSelect={() => handleSelectOption(option.value)}
                            className="flex w-full items-center gap-2"
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedValue === option.value
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {renderOption ? renderOption(option) : option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ) : null}
                    {!isLoading && items.length === 0 ? (
                      <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                        {emptyMessage}
                      </CommandPrimitive.Empty>
                    ) : null}
                  </CommandList>
                </div>
              </div>
            </div>
          </div>
        </CommandPrimitive>
      </div>
    );

    return inputElement;
  },
);
